import { ShieldCheck, Tag, Zap, Heart, ArrowRight, Phone } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { PageHeroCard } from "@/components/site/PageHeroCard";
import { SamplingLocationsSection } from "@/components/site/SamplingLocationsSection";
import { Button } from "@/components/ui/button";
import { useLang } from "@/i18n/LangContext";
import aboutHero from "@/assets/jarntorget.avif";
import { PageMeta } from "@/components/site/PageMeta";

const OmOss = () => {
  const { t, lang } = useLang();
  const a = t.about;
  const bookingHref = lang === "sv" ? "/boka-tid" : "/en/book-appointment";
  const contactHref = lang === "sv" ? "/kontakt" : "/en/contact";
  const icons = [ShieldCheck, Tag, Zap, Heart];


  const title = lang === "sv" ? "Om oss | Intygsläkaren" : "About us | Intygsläkaren";
  const desc =
    lang === "sv"
      ? "Lär känna Intygsläkaren — specialistmottagning i Göteborg för läkarintyg och medicinska utredningar."
      : "Get to know Intygsläkaren — a specialist clinic in Gothenburg for medical certificates and assessments.";

  return (
    <div className="min-h-screen bg-background">
      <PageMeta title={title} description={desc} />
      <Header />
      <main>
        <PageHeroCard imageSrc={aboutHero} imageAlt={lang === "sv" ? "Mottagningen Intygsläkaren" : "Intygsläkaren clinic"} compact>
          <div className="animate-fade-up max-w-2xl">
            <h1 className="text-balance font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              {a.title}
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">{a.subtitle}</p>
          </div>
        </PageHeroCard>

        <section className="py-14 lg:py-20">
          <div className="container-wide">
            <div className="mx-auto max-w-3xl">
              <h2 className="font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
                {a.introTitle}
              </h2>
              <p className="mt-5 text-base leading-relaxed text-muted-foreground sm:text-lg">{a.introText1}</p>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">{a.introText2}</p>
            </div>
          </div>
        </section>

        <section className="pb-14 lg:pb-20">
          <div className="container-wide">
            <div className="mx-auto max-w-3xl text-center">
              <h2 className="font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
                {a.valuesTitle}
              </h2>
            </div>
            <div className="mt-12 grid gap-6 md:grid-cols-2">
              {a.values.map((v, i) => {
                const Icon = icons[i] ?? ShieldCheck;
                return (
                  <div key={v.title} className="rounded-2xl border border-border bg-card sm:rounded-3xl p-8 shadow-card">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                      <Icon className="h-6 w-6" />
                    </div>
                    <h3 className="mt-5 font-display text-xl font-semibold text-foreground">{v.title}</h3>
                    <p className="mt-3 text-sm text-muted-foreground">{v.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <SamplingLocationsSection className="bg-muted/20" />

        <section className="px-2 py-16 sm:px-3 lg:px-4 lg:py-20">
          <div className="relative mx-auto w-full max-w-[96rem] overflow-hidden rounded-2xl bg-primary px-8 py-14 text-primary-foreground shadow-elevated sm:rounded-3xl lg:px-16 lg:py-20">
              <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
              <div className="absolute -bottom-24 -left-10 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
              <div className="relative max-w-2xl">
                <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">{a.ctaTitle}</h2>
                <p className="mt-3 text-base text-primary-foreground/85">{a.ctaText}</p>
                <div className="mt-7 flex flex-wrap gap-3">
                  <Button asChild variant="onPrimary" size="xl">
                    <a href={bookingHref}>
                      {a.ctaPrimary}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </a>
                  </Button>
                  <Button asChild variant="ghostOnPrimary" size="xl">
                    <a href={contactHref}>
                      <Phone className="mr-2 h-4 w-4" />
                      {a.ctaSecondary}
                    </a>
                  </Button>
                </div>
              </div>
            </div>
        </section>

        <Breadcrumbs
          items={[
            { label: lang === "sv" ? "Hem" : "Home", to: lang === "sv" ? "/" : "/en" },
            { label: a.eyebrow },
          ]}
        />
      </main>
      <Footer />
    </div>
  );
};

export default OmOss;
